//@flow
import React from 'react';
import {
    Box,
    Text,
    Columns,
    Column,
    Icon as NeoIcon
} from '@locus-taxy/neo-ui';
import { type ThemeType } from 'react-css-themr';
import { type TFunction } from 'react-i18next';

import TooltipContent, {
    TooltipHeader,
    TooltipBody,
    TooltipItem,
    TooltipSection
} from 'common/components/common/TooltipContent';
import { type FpBeat } from 'common/models/internal/FpBeat';
import UnitConversionHelper from 'common/helpers/UnitConversionHelper';

type BeatInfoTooltipContentProps = {
    beat: FpBeat,
    t: TFunction,
    theme: ThemeType,
    UnitConverter: UnitConversionHelper
};
class BeatInfoTooltipContent extends React.PureComponent<BeatInfoTooltipContentProps> {
    render() {
        const { beat, UnitConverter, t } = this.props;
        const formattedTat = UnitConverter.convertSeconds({
            roundOff: true,
            value: beat.summary.totalTransactionDuration
        }).valueString;

        const formattedTransactionDuration = UnitConverter.convertSeconds({
            roundOff: true,
            value: beat.summary.totalTravelDuration
        }).valueString;
        const formattedDistance = UnitConverter.convertMeters({
            value: beat.summary.totalTravelDistance,
            pluralizeUnit: true,
            roundOff: true,
            decimals: 2,
            showUnitAbbr: false,
            padLeft: true
        }).valueString;
        const { totalVolume, multiTripVolume } = beat.summary;
        return (
            <TooltipContent minWidth={300}>
                <TooltipHeader
                    value={
                        <Box mb={1} mt={2}>
                            <Columns space="xSmall" alignY="center">
                                <NeoIcon
                                    icon="information"
                                    color="grey.500"
                                    size="medium"
                                />
                                <Text fontSize={2} fontWeight={600}>
                                    {beat.name}
                                </Text>
                            </Columns>
                        </Box>
                    }
                />
                <TooltipBody>
                    <TooltipSection>
                        <Columns wrap width="fluid">
                            <Column width="fluid">
                                <TooltipItem>
                                    <TooltipItem.Value
                                        inline
                                        value={
                                            <Columns
                                                space="small"
                                                alignY="center"
                                            >
                                                <NeoIcon
                                                    icon="dispatcher_tasks"
                                                    color="grey.500"
                                                    size="medium"
                                                />
                                                <Text>
                                                    {t('fpPlanView.tasks')}
                                                </Text>
                                                <Text
                                                    fontSize={1}
                                                    fontWeight="medium"
                                                >
                                                    {beat.summary.visitCount}
                                                </Text>
                                            </Columns>
                                        }
                                    />
                                </TooltipItem>
                            </Column>
                        </Columns>
                    </TooltipSection>
                    <TooltipSection>
                        <Columns>
                            <Column width="content">
                                <TooltipItem>
                                    <TooltipItem.Value
                                        inline
                                        value={
                                            <Columns
                                                space="xSmall"
                                                alignY="center"
                                            >
                                                <NeoIcon
                                                    icon="clock"
                                                    color="grey.500"
                                                    size="medium"
                                                />
                                                <Text>
                                                    {t('fpPlanView.tat')}
                                                </Text>
                                                <Text
                                                    fontSize={1}
                                                    fontWeight="medium"
                                                >
                                                    {formattedTat}
                                                </Text>
                                            </Columns>
                                        }
                                    />
                                </TooltipItem>
                            </Column>
                        </Columns>
                    </TooltipSection>
                    <TooltipSection>
                        <Columns wrap width="fluid">
                            <Column width="fluid">
                                <TooltipItem>
                                    <TooltipItem.Value
                                        value={
                                            <Columns
                                                space="xSmall"
                                                alignY="center"
                                            >
                                                <NeoIcon
                                                    icon="motiontrack_distance"
                                                    color="grey.500"
                                                    size="medium"
                                                />
                                                <Text>
                                                    {t(
                                                        'fpPlanView.totalTravelDistanceValue'
                                                    )}
                                                </Text>
                                                <Text
                                                    fontSize={1}
                                                    fontWeight="medium"
                                                >
                                                    {formattedDistance}
                                                </Text>
                                            </Columns>
                                        }
                                    />
                                </TooltipItem>
                            </Column>
                        </Columns>
                    </TooltipSection>
                    <TooltipSection>
                        <Columns>
                            <Column width="content">
                                <TooltipItem>
                                    <TooltipItem.Value
                                        value={
                                            <Columns
                                                space="xSmall"
                                                alignY="center"
                                            >
                                                <NeoIcon
                                                    icon="motiontrack_distance"
                                                    color="grey.500"
                                                    size="medium"
                                                />
                                                <Text>
                                                    {t(
                                                        'fpPlanView.totalTransactionDuration'
                                                    )}
                                                </Text>
                                                <Text
                                                    fontSize={1}
                                                    fontWeight="medium"
                                                >
                                                    {
                                                        formattedTransactionDuration
                                                    }
                                                </Text>
                                            </Columns>
                                        }
                                    />
                                </TooltipItem>
                            </Column>
                        </Columns>
                    </TooltipSection>
                    <TooltipSection>
                        <Columns>
                            <Column width="content">
                                <TooltipItem>
                                    <TooltipItem.Value
                                        inline
                                        value={
                                            <Columns
                                                space="xSmall"
                                                alignY="center"
                                            >
                                                <NeoIcon
                                                    icon="dispatcher_volume"
                                                    color="grey.500"
                                                    size="medium"
                                                />
                                                <Text>
                                                    {t(
                                                        'fpPlanView.totalBeatVolume'
                                                    )}
                                                </Text>
                                                <Text
                                                    fontSize={1}
                                                    fontWeight="medium"
                                                >
                                                    {totalVolume}
                                                </Text>
                                            </Columns>
                                        }
                                    />
                                </TooltipItem>
                            </Column>
                        </Columns>
                    </TooltipSection>
                    {multiTripVolume?.map((tripVolume, index) => (
                        <TooltipSection key={index}>
                            <Columns>
                                <Column width="content">
                                    <TooltipItem>
                                        <TooltipItem.Value
                                            inline
                                            value={
                                                <Columns
                                                    space="xSmall"
                                                    alignY="center"
                                                >
                                                    <Box pl={6}>
                                                        <Text>
                                                            {beat.name.replace(
                                                                'beat',
                                                                t(
                                                                    'fpPlanView.trip'
                                                                )
                                                            )}
                                                            .{index + 1}
                                                        </Text>
                                                    </Box>
                                                    <Text
                                                        fontSize={1}
                                                        fontWeight="medium"
                                                    >
                                                        {tripVolume}
                                                    </Text>
                                                </Columns>
                                            }
                                        />
                                    </TooltipItem>
                                </Column>
                            </Columns>
                        </TooltipSection>
                    ))}
                </TooltipBody>
            </TooltipContent>
        );
    }
}

export default BeatInfoTooltipContent;
